/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect } from 'react';

import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import TooltipContainer from '@atlaskit/tooltip/TooltipContainer';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

const tooltipInlineStyles: React.CSSProperties = {
	bottom: '100%',
	left: '50%',
};

const wrapperStyles = css({
	position: 'relative',
	display: 'inline-flex',
});

const tooltipPositionStyles = css({
	position: 'absolute',
	marginBottom: token('space.050'),
	transform: 'translateX(-50%)',
});

type DismissableTooltipProps = {
	children: React.ReactNode;
	content: React.ReactNode;
	isVisible: boolean;
	onDismiss: () => void;
	dismissAfter?: number;
	dismissKey?: number;
	testId?: string;
};

export const DismissableTooltip = ({
	children,
	content,
	isVisible,
	onDismiss,
	dismissAfter = 1000,
	dismissKey,
	testId,
}: DismissableTooltipProps): React.JSX.Element => {
	useEffect(() => {
		if (!isVisible) {
			return;
		}

		const timeoutId = window.setTimeout(onDismiss, dismissAfter);

		return () => window.clearTimeout(timeoutId);
	}, [dismissAfter, dismissKey, isVisible, onDismiss]);

	return (
		<span css={wrapperStyles}>
			<VisuallyHidden role="status" aria-live="assertive" aria-atomic="true">
				{isVisible && content}
			</VisuallyHidden>
			{children}
			{isVisible && (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- @atlaskit/css does not support percentage positioning values required to align this tooltip.
				<span css={tooltipPositionStyles} style={tooltipInlineStyles}>
					<TooltipContainer placement="top" testId={testId}>
						{content}
					</TooltipContainer>
				</span>
			)}
		</span>
	);
};
